import { useEffect, useState } from "react";
import type { OrgRolesDto, RoleDef } from "@gatiq/shared";
import { api } from "@/lib/api";
import { useAuth } from "./AuthProvider";

/*
 * One fetch per signed-in person, shared by every component that asks: the
 * sidebar, the team pages and the settings screens all read the same list.
 * Keyed by user so switching accounts (or impersonating) never shows the
 * previous org's roles.
 */
let cache: { key: string; request: Promise<OrgRolesDto> } | null = null;
const EVENT = "org-roles-updated";

/** Call after changing levels, names or permissions so every reader refetches. */
export function invalidateOrgRoles(): void {
  cache = null;
  window.dispatchEvent(new Event(EVENT));
}

/** The org's roles — names, levels, permissions — or null while loading / outside an org. */
export function useOrgRoles(): OrgRolesDto | null {
  const { user } = useAuth();
  const [data, setData] = useState<OrgRolesDto | null>(null);
  const key = user && user.orgId && user.role !== "karigar" ? `${user.id}:${user.orgId}` : null;

  useEffect(() => {
    if (!key) return;
    let live = true;
    const load = (): void => {
      if (cache?.key !== key) cache = { key, request: api.get<OrgRolesDto>("/org/roles") };
      const { request } = cache;
      request.then(
        (res) => {
          if (live) setData(res);
        },
        () => {
          if (cache?.request === request) cache = null;
        },
      );
    };
    load();
    window.addEventListener(EVENT, load);
    return () => {
      live = false;
      window.removeEventListener(EVENT, load);
    };
  }, [key]);

  return key ? data : null;
}

/** "Distributor 2" → the org's own name for it, e.g. "Super Stockist". */
export function roleName(roles: OrgRolesDto | null, base: string, level?: number | null): string | null {
  return roles?.roles.find((r: RoleDef) => r.base === base && r.level === (level ?? 1))?.name ?? null;
}
